import React, { Dispatch, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { UiButton } from 'src/components/UI/Button';
import { SidebarOrganizerOrganizationType } from 'src/models/Partner/Sidebar';
import { useRefClickOutside } from 'src/utils/hooks/useRefClickOutside';
import { useBemHelper } from 'src/utils/hooks/useBemHelper';
import { MenuCloseIcon } from 'src/components/Icons/Menu/Close';

type SidebarOrganizationsType = {
  organizations: Array<SidebarOrganizerOrganizationType>;
  toggleOrganizationsList: () => void;
  changeCurrentOrg: (orgUrl: string) => void;
  isMobile: boolean;
  setHovered: Dispatch<boolean>;
};

export const SidebarOrganizationsList: React.FC<SidebarOrganizationsType> = ({
  organizations,
  toggleOrganizationsList,
  changeCurrentOrg,
  isMobile,
  setHovered,
}: SidebarOrganizationsType) => {
  const bemHelper = useBemHelper('sidebar-organizations-list');

  const ref = useRefClickOutside<HTMLDivElement>(true, toggleOrganizationsList, 10);

  const { t } = useTranslation();

  const onClickItem = useCallback(
    (org: SidebarOrganizerOrganizationType) => () => {
      toggleOrganizationsList();
      changeCurrentOrg(org.url);
    },
    [changeCurrentOrg, toggleOrganizationsList]
  );

  const [show, setShow] = useState(true);

  const closeListWithAnimation = () => setShow(false);

  const modifiers = [];
  if (isMobile) {
    modifiers.push('is-modal');
    modifiers.push(show ? 'opened' : 'closed');
  }

  const localSetHovered = (hovered: boolean) => () => setHovered(hovered);

  return (
    <div
      className={classNames(bemHelper(modifiers))}
      ref={ref}
      onMouseEnter={localSetHovered(true)}
      onMouseLeave={localSetHovered(false)}
    >
      {isMobile && (
        <div
          role="presentation"
          className={classNames(bemHelper('backdrop'), 'fadeIn', { fadeOut: !show })}
          onClick={closeListWithAnimation}
        />
      )}
      <div
        className={classNames(bemHelper('inner'), { fadeIn: !isMobile })}
        onAnimationEnd={() => {
          if (!show) {
            toggleOrganizationsList();
          }
        }}
      >
        {isMobile && (
          <>
            <div className={bemHelper('header')}>
              {t('leftMenu.switchOrganization')}&nbsp;
              <MenuCloseIcon onClick={closeListWithAnimation} className={bemHelper('header-icon')} />
            </div>

            <hr className={bemHelper('divider')} />
          </>
        )}
        {organizations.map((org) => (
          <UiButton
            key={org.name}
            tag="a"
            className={bemHelper('item', org.isCurrentOrganization && ['selected'])}
            disabled={org.isCurrentOrganization}
            onClick={onClickItem(org)}
            title={org.name}
          >
            {org.name}
          </UiButton>
        ))}
      </div>
    </div>
  );
};
